/**
 * Cookie consent state.
 *
 * Only analytics is gated. Everything else the app stores — the access token, booking drafts,
 * pending OAuth state — is strictly necessary to deliver a feature the visitor asked for, and
 * under GDPR/ePrivacy that does not require consent. Presenting it as optional would be
 * misleading, since declining could not actually switch it off.
 *
 * The choice itself is kept in localStorage rather than a cookie: it is first-party, never
 * sent to the server, and storing a consent record is itself "strictly necessary".
 */

const STORAGE_KEY = "bc_cookie_consent";
const VERSION = 1;

export type ConsentDecision = "accepted" | "rejected";

interface StoredConsent {
  version: number;
  decision: ConsentDecision;
  decidedAt: string;
}

type Listener = (decision: ConsentDecision | null) => void;
const listeners = new Set<Listener>();

/** Reads the stored decision. Returns null when the visitor has not chosen yet. */
export function getConsent(): ConsentDecision | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    // A bumped VERSION invalidates old records, which is how we re-ask if what we
    // collect ever changes.
    if (parsed.version !== VERSION) return null;
    if (parsed.decision !== "accepted" && parsed.decision !== "rejected") return null;
    return parsed.decision;
  } catch {
    // Private mode, disabled storage, or corrupt JSON — treat as undecided rather than
    // assuming consent.
    return null;
  }
}

export function hasAnalyticsConsent(): boolean {
  return getConsent() === "accepted";
}

/** Records a decision and notifies subscribers. */
export function setConsent(decision: ConsentDecision): void {
  try {
    const record: StoredConsent = {
      version: VERSION,
      decision,
      decidedAt: new Date().toISOString(),
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage unavailable: the banner will reappear next visit, which is the safe failure.
  }
  listeners.forEach((fn) => fn(decision));
  notifyDocument(decision);
  if (decision !== "accepted") clearAnalyticsCookies();
}

const ANALYTICS_COOKIE = /^(_ga|_ga_[A-Za-z0-9]+|_clck|_clsk)$/;

/**
 * Delete the first-party analytics cookies an earlier Accept created. Runs after the listeners
 * have switched both tags to "denied", so neither writes them back. gtag and Clarity set these
 * on the registrable domain (".bunnycal.io"), so every parent domain is tried alongside the
 * host-only form. Cookies on Microsoft's own domains are not ours to touch and are left alone.
 */
export function clearAnalyticsCookies(): void {
  try {
    const names = document.cookie
      .split(";")
      .map((c) => c.split("=")[0].trim())
      .filter((n) => ANALYTICS_COOKIE.test(n));
    if (!names.length) return;
    const parts = window.location.hostname.split(".");
    const domains = [""];
    for (let i = 0; i < parts.length - 1; i++) domains.push(`; domain=.${parts.slice(i).join(".")}`);
    for (const name of names) {
      for (const d of domains) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${d}`;
      }
    }
  } catch {
    /* cookies unavailable — nothing was stored */
  }
}

/**
 * The Hugo blog and docs listen for this on their own pages. They cannot share this module,
 * but they read the same localStorage key, so dispatching keeps the two implementations in
 * step within a single document too.
 */
function notifyDocument(decision: ConsentDecision | null) {
  try {
    window.dispatchEvent(new CustomEvent("bc:consent", { detail: decision }));
  } catch {
    /* CustomEvent unavailable — the stored value is still the source of truth. */
  }
}

/** Clears the decision so the banner is shown again. Used by "Manage cookies". */
export function resetConsent(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to clear */
  }
  listeners.forEach((fn) => fn(null));
  notifyDocument(null);
  clearAnalyticsCookies();
}

export function subscribeToConsent(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
